import { isEqual, isEmpty, map, get, filter } from 'lodash';
import turf from '@turf/turf';
import csv from 'csvtojson';

import { isNumericalString, checkValueIsNumber, checkArrayForNumbers } from '../check-for-number';
import { readUploadedFileAsText } from '../export-import-files';
import { pointsCollectionFeatureToLineString } from '../geojson-utilities';

const DEFAULT_GPSCP_ARRAY = [0, 0, 0];
const DEFAULT_ATTITUDE_ARRAY = [0, 0, 0, 0];
const MAX_NUMBER_OF_POINTS = 2400;
const CSV_HEADERS_ARRAY = [
  'ID',
  'URL',
  'Video_Time',
  'GNSS_Day',
  'GNSS_Hour',
  'Long_Home',
  'Lat_Home',
  'H_Home',
  'Long_CP',
  'Lat_CP',
  'H_CP',
  'H_PN',
  'Omega_CP',
  'Phi_CP',
  'Kappa_CP',
  'Orientation_CP',
  'Long_CPP',
  'Lat_CPP',
  'H_CPP',
  'Long_P0',
  'Lat_P0',
  'H_P0',
  'Long_P1',
  'Lat_P1',
  'H_P1',
  'Long_P2',
  'Lat_P2',
  'H_P2',
  'Long_P3',
  'Lat_P3',
  'H_P3'
];

/**
 * Takes a csv file and creates the track for the video from the columns, which must be defined as follows. Also creates
 * the control points and the array of points of the flight, and checks if the corners of the image are defined.
 *
 * ['ID','URL','Video_Time','GNSS_Day','GNSS_Hour','Long_Home','Lat_Home','H_Home','Long_CP','Lat_CP','H_CP',
 * 'H_PN','Omega_CP','Phi_CP','Kappa_CP','Orientation_CP','Long_CPP','Lat_CPP','H_CPP',
 * 'Long_P0','Lat_P0','H_P0','Long_P1','Lat_P1','H_P1','Long_P2','Lat_P2','H_P2','Long_P3','Lat_P3','H_P3'].
 *
 * @async
 * @param {File} file - The csv file to read.
 * @returns {object} An object containing the array of feature Points, a feature LineString representing the video track,
 * a Feature Collection of Points with the control points and a flag to check if the corrdinates are defined.
 * @throws {Error} Error if anything goes wrong or file is not kml.
 */
const createTrackFromCSV = async file => {
  try {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length);

    if (!isEqual(fileExtension, 'csv'))
      if (!isEqual(fileExtension, 'CSV')) throw new Error('No valid file extension');

    const csvText = await readUploadedFileAsText(file);

    const csvDataArray = await csv({
      noheader: false,
      headers: CSV_HEADERS_ARRAY
    }).fromString(csvText);

    if (csvDataArray.length >= MAX_NUMBER_OF_POINTS)
      throw new Error('This file is too large. Please, pick another one.');

    let areCoordinatesUndefined = false;
    const newFeatureArray = map(csvDataArray, (csvRow, index, array) => {
      if (!isNumericalString(csvRow.Long_CP) || !isNumericalString(csvRow.Lat_CP))
        throw new Error('Invalid CSV file, check for errors');

      if (!areCoordinatesUndefined) {
        const undefinedCoordinates = filter(
          [
            csvRow.Long_P0,
            csvRow.Lat_P0,
            csvRow.Long_P1,
            csvRow.Lat_P1,
            csvRow.Long_P2,
            csvRow.Lat_P2,
            csvRow.Long_P3,
            csvRow.Lat_P3
          ],
          coordinate => !isNumericalString(coordinate)
        );
        areCoordinatesUndefined = !isEmpty(undefinedCoordinates);
      }

      const GPSCP = checkArrayForNumbers({
        valuesArray: [csvRow.Long_CP, csvRow.Lat_CP, csvRow.H_CP],
        defaultValuesArray: DEFAULT_GPSCP_ARRAY
      });

      const properties = {
        ATTCP: checkArrayForNumbers({
          valuesArray: [csvRow.Omega_CP, csvRow.Phi_CP, csvRow.Kappa_CP, csvRow.Orientation_CP],
          defaultValuesArray: DEFAULT_ATTITUDE_ARRAY
        }),
        coordinates: [
          checkArrayForNumbers({
            valuesArray: [csvRow.Long_P0, csvRow.Lat_P0, csvRow.H_P0],
            defaultValuesArray: GPSCP
          }),
          checkArrayForNumbers({
            valuesArray: [csvRow.Long_P1, csvRow.Lat_P1, csvRow.H_P1],
            defaultValuesArray: GPSCP
          }),
          checkArrayForNumbers({
            valuesArray: [csvRow.Long_P2, csvRow.Lat_P2, csvRow.H_P2],
            defaultValuesArray: GPSCP
          }),
          checkArrayForNumbers({
            valuesArray: [csvRow.Long_P3, csvRow.Lat_P3, csvRow.H_P3],
            defaultValuesArray: GPSCP
          })
        ],
        distanceTravelled: 0,
        GPSHome: checkArrayForNumbers({
          valuesArray: [csvRow.Long_Home, csvRow.Lat_Home, csvRow.H_Home],
          defaultValuesArray: GPSCP
        }),
        GPSCPP: checkArrayForNumbers({
          valuesArray: [csvRow.Long_CPP, csvRow.Lat_CPP, csvRow.H_CPP],
          defaultValuesArray: GPSCP
        }),
        HPN: checkValueIsNumber({ value: csvRow.H_PN, defaultValue: 0 }),
        isControlPoint: index === 0 || index === array.length - 1,
        timeUS: checkValueIsNumber({ value: csvRow.Video_Time, defaultValue: 0 })
      };

      return turf.point(GPSCP, properties);
    });

    const newTrack = pointsCollectionFeatureToLineString(turf.featureCollection(newFeatureArray));

    const newControlPoints = turf.featureCollection(
      filter(newFeatureArray, feature => get(feature, 'properties.isControlPoint'))
    );

    return {
      newFeatureArray,
      newTrack,
      newControlPoints,
      areCoordinatesUndefined
    };
  } catch (error) {
    throw new Error(error.message);
  }
};

export default createTrackFromCSV;
